/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { selectAllResources, selectResourceById } from "../../slices/resourceCollection/resourceCollectionSlice";
import { selectEditMode } from "../../slices/editMode/editModeSlice";
import { selectAllResourceChanges } from "../../slices/resourceChangeReport/resourceChangeReportSlice";
import generateResourceTextNarrative from "../compositionResourceFunctions/generateResourceTextNarrative";
import { emptyReasonCodeableConceptSet, emptyReasonNotStarted, emptyTextNoData, sectionCodeCodeableConceptSet } from "../../dictionaries/CodeSystemLookup";
import updateChangeReport from "../../slices/resourceChangeReport/updateChangeReport";
import { TextField } from "@mui/material";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import CodeableConceptEntry, { simpleDisplayStringFromFHIRCodeableConcept } from "../../datatypeComponents/CodeableConceptEntry";
import SectionEntryArrayEntry from "./SectionEntryArrayEntry";
import ReferenceEntry, { DisplayFromFHIRReference } from "../../datatypeComponents/ReferenceEntry";
import { Button } from "semantic-ui-react";
import EditSectionArrayModal from "./EditSectionArrayModal";
import SectionDisplay from "./SectionDisplay";
import NarrativeEntry from "../../datatypeComponents/NarrativeEntry";
import { selectTriggerRefresh } from "../../slices/triggerRefresh/triggerRefreshSlice";
import { selectTopSectionById, topSectionUpdated } from "../../slices/topSections/topSectionsSlice";
import adjustEmptyReason from "../compositionResourceFunctions/adjustEmptyReason";

const days = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const dayOfWeekLookup = { "Sunday": 0, "sunday": 0, "Monday": 1, "monday": 1, "Tuesday": 2, "tuesday": 2, "Wednesday": 3, "wednesday": 3, "Thursday": 4, "thursday": 4, "Friday": 5, "friday": 5, "Saturday": 6, "saturday": 6 };
//This isn't perfect, there is a small window where this will give an inaccurate converted time. If someone further east than UTC+2 is looking at Monday the week before the daylight savings time switch in ET.
// The most likely scenario for it being wrong would be someone in Asia looking at Monday the week before the daylight savings time switch for Eastern US.
const getConvertedFutureTime = (day, timeHours, timeMinutes = 0, rejectIfSameTime = false) => {
    let localNextOfThatDayOfWeek = new Date();
    localNextOfThatDayOfWeek.setDate(localNextOfThatDayOfWeek.getDate() + (dayOfWeekLookup[day] + 7 - localNextOfThatDayOfWeek.getDay()) % 7);
    localNextOfThatDayOfWeek.setHours(timeHours, timeMinutes, 0, 0);
    let newYorkTime = new Date(localNextOfThatDayOfWeek).toLocaleString("en-US", { timeZone: "America/New_York" });
    localNextOfThatDayOfWeek.setMilliseconds(localNextOfThatDayOfWeek - new Date(newYorkTime));
    if (rejectIfSameTime && localNextOfThatDayOfWeek.toString() === (new Date(newYorkTime)).toString()) {
        return false;
    }
    return localNextOfThatDayOfWeek;
}

const getTimeAndAmOrPM = (singleTime) => {
    let time;
    let pm = false;
    let error = false;
    let beforeLastTwoCharacters = singleTime.substring(0, singleTime.length - 2).trim();
    if (beforeLastTwoCharacters !== "" && !isNaN(beforeLastTwoCharacters)) {
        let lastTwoCharacters = singleTime.substring(singleTime.length - 2);
        time = beforeLastTwoCharacters;
        if (lastTwoCharacters === "am") {
            //It's am
        } else if (lastTwoCharacters === "pm") {
            pm = true;
        } else {
            error = true;
            console.log("Error, couldn't find am or pm");
        }
    }
    if (time) {
        time = parseInt(time);
    }
    return [time, pm, error];
}

const processTimeTableRow = (row, longTimeZone = "", shortTimeZone = "") => {
    let error = false;
    let differentDay = "";
    let cells = row.replace("<tr>", "").replace("</tr>", "").split("<td");
    cells = cells.slice(1);
    let originalTimeCell = cells[0];
    originalTimeCell = (originalTimeCell.substring(originalTimeCell.indexOf(">") + 1, originalTimeCell.indexOf("</td"))).replaceAll("\n", " ").replaceAll("  ", " ").trim().toLowerCase();
    let dayOfWeek = originalTimeCell.split(" ")[0];
    let convertedTimeString = "";
    if (dayOfWeekLookup[dayOfWeek]) {
        let endTime;
        let startTime;
        let pm = false; //It's an AM time unless shown otherwise
        let restOfCell = originalTimeCell.substring(dayOfWeek.length + 1).replaceAll(" - ", "-").replaceAll(" -", "-").replaceAll("- ", "-").trim();
        let hyphenIndex = restOfCell.indexOf("-");
        if (hyphenIndex !== -1 && hyphenIndex !== 0) {
            let firstHalf = restOfCell.substring(0, hyphenIndex).replaceAll(".", "").trim();
            let secondHalf = restOfCell.substring(hyphenIndex + 1).replaceAll(".", "").trim();
            if (firstHalf !== "" && !isNaN(firstHalf)) {
                startTime = parseInt(firstHalf);
                //Need to get whether it's am or pm by looking at end time
                [endTime, pm, error] = getTimeAndAmOrPM(secondHalf);
                if (endTime && startTime > endTime && pm) {
                    //This may indicate that something is wrong, otherwise this is a VERY VERY long meeting
                    pm = false;	//So assuming that the first time is am
                }
            } else {
                //Possibly has the am or pm in the first half
                [startTime, pm, error] = getTimeAndAmOrPM(firstHalf);
            }
        } else {
            //assumes that this is just a start time and no end time specified
            [startTime, pm, error] = getTimeAndAmOrPM(restOfCell);
        }

        if (error === false && startTime) {
            if (pm === false && startTime === 12) {
                //Then it's midnight
                //startTime = 0;
                console.log("Warning, the start time is midnight. Defaulting to noon time, if you don't want this please uncomment code above.");
            } else if (pm && startTime !== 12) {
                startTime += 12;    //If it's pm and not noon, then add 12 to the time
            }
            let localTime = getConvertedFutureTime(dayOfWeek, startTime, 0, true);
            if (localTime) {
                convertedTimeString = localTime.toLocaleTimeString(navigator.language, { hour: '2-digit', minute: '2-digit' }); //It will show something like 3:00pm or 15:00 depending on the language settings

                //Checks if day of the week is different, for example 8pm ET on Monday is going to be 2am Prague Time on Tuesday
                if (localTime.getDay() !== dayOfWeekLookup[dayOfWeek]) {
                    differentDay = days[localTime.getDay()] + " ";
                }
            }
        }
    } else {
        error = true;
        console.log("Couldn't find day of week: " + dayOfWeek);
    }
    let localTimeCell = "<td>" + differentDay + convertedTimeString.toLowerCase() + " [" + shortTimeZone + "]</td>";
    let newRow = "<tr>";
    for (let i = 0; i < cells.length; i++) {
        let cell = "<td" + cells[i];
        newRow += cell;
        if (i === 0) {
            newRow += localTimeCell
        }
    }
    newRow += "</tr>";
    if (error) {
        return false;
    }
    return newRow;
}

const convertTimeTable = (detail) => {
    let localTime = getConvertedFutureTime("Tuesday", 19, 0, true); //Just to check if different time zone
    if (localTime) {
        let timeTable = detail;
        let longTimeZone = localTime.toLocaleDateString(undefined, { day: '2-digit', timeZoneName: 'long' }).substring(4); //Will say something like Central European Summer Time
        let shortTimeZone = localTime.toLocaleDateString(undefined, { day: '2-digit', timeZoneName: 'short' }).substring(4); //Will say something like GMT+2 or CDT
        let timeZone = longTimeZone || shortTimeZone;
        timeZone = timeZone.replace(" Time", "");
        timeTable = timeTable.replace("<th>Day/Time (Eastern US)</th>", "<th>Day/Time (Eastern US)</th><th>(" + timeZone + ")</th>");

        let tbodyStart = timeTable.indexOf("<tbody>") + 7;
        let tbodyEnd = timeTable.indexOf("</tbody>");
        let tbody = timeTable.substring(tbodyStart, tbodyEnd);

        let rows = tbody.split("<tr");
        rows = rows.slice(1);
        let changedTbody = "";
        for (let i = 0; i < rows.length; i++) {
            let row = "<tr" + rows[i];
            row = processTimeTableRow(row, longTimeZone, shortTimeZone);
            if (row) {
                changedTbody += row;
            } else {
                return false;
            }
        }
        timeTable = timeTable.substring(0, tbodyStart) + changedTbody + timeTable.substring(tbodyEnd);

        return timeTable;
    }
    return false;
}

const TopSectionSliceDisplay = ({ foi, setCompositionResourceState, sectionIndex, resourceReferenceMap,
    sectionNavigationElementIds, associatedOutcomeMeasureReportState }) => {

    const topSection = useSelector(state => selectTopSectionById(state, sectionIndex));
    //entity: {  
    //     id: sectionIndex,
    //     displayTitle: section.title || section.code..display,
    //     compositionId: parent FOI,
    //     productFamily: ProfileName,
    //     initialSection: sectionJson,
    //     revisedSection: sectionJson
    // 
    //     sectionJson = { extension, title, code, author, focus, text, orderedBy, entry, emptyReason, section }
    const topSectionJson = topSection?.revisedSection;
    const compositionId = topSection?.compositionId;
    const compositionType = topSection?.productFamily || "Composition";
    const compositionTitle = resourceReferenceMap?.compositionTitle;
    const editMode = useSelector(state => selectEditMode(state));
    const dispatch = useDispatch();
    const resourceChanges = useSelector(state => selectAllResourceChanges(state));
    const resourceCollection = useSelector(state => selectAllResources(state));
    const triggerRefresh = useSelector(selectTriggerRefresh);
    const parentResourceDetail = useSelector(state => selectResourceById(state, foi));

    const [sectionState, setSectionState] = useState({});
    const [generatedNarrativeState, setGeneratedNarrativeState] = useState("");
    const [editSectionModal, setEditSectionModal] = useState({ "openModal": false });
    const [headerDisplayState, setHeaderDisplayState] = useState("");
    const [sectionLoaded, setSectionLoaded] = useState({});
    const [noneditableNetEffectSectionState, setNoneditableNetEffectSectionState] = useState(false);

    useEffect(() => {
        if (topSectionJson && Object.keys(topSectionJson).length > 0) {
            let section = JSON.parse(JSON.stringify(topSectionJson));
            if (sectionIndex !== topSection.id) {
                alert("sectionIndex " + sectionIndex + " but topSection.id is " + topSection.id);
            }
            let startingSectionStateValues = {
                "sectionIndex": topSection.id,
                "editTitle": false,
                "extension": section.extension || [],
                "title": section.title || "",
                "code": section.code || null,
                "author": section.author || [],
                "focus": section.focus || null,
                "text": section.text || {
                    "status": "empty",
                    "div": '<div xmlns="http://www.w3.org/1999/xhtml"><p>[No narrative text.]</p></div>'
                },
                "orderedBy": section.focus || null,
                "entry": section.entry || [],
                "emptyReason": section.emptyReason || null,
                "section": section?.section || []
            };


            let autoChangedEmptyReasonChanged = "no change";
            let sectionIsEmpty = true;
            if (startingSectionStateValues.section?.length) {
                sectionIsEmpty = false;
            } else if (startingSectionStateValues.entry?.length) {
                sectionIsEmpty = false;
            } else if (startingSectionStateValues.text?.status && startingSectionStateValues.text.status !== "empty") {
                sectionIsEmpty = false;
            }
            if (sectionIsEmpty && !startingSectionStateValues.emptyReason) {
                autoChangedEmptyReasonChanged = JSON.parse(JSON.stringify(emptyReasonNotStarted));
                startingSectionStateValues.emptyReason = autoChangedEmptyReasonChanged;
            }
            if (!sectionIsEmpty && startingSectionStateValues.emptyReason) {
                autoChangedEmptyReasonChanged = null;
                startingSectionStateValues.emptyReason = null;
            }
            setSectionState(startingSectionStateValues);
            if (autoChangedEmptyReasonChanged !== "no change" && editMode) {
                let newTopSectionDetail = JSON.parse(JSON.stringify(topSection));
                let previousInitialValueForEmptyReason = null;
                let previousFinalValueForEmptyReason = null;
                previousInitialValueForEmptyReason = newTopSectionDetail.initialSection?.emptyReason || null;
                previousFinalValueForEmptyReason = JSON.stringify(newTopSectionDetail.revisedSection?.emptyReason || null);
                newTopSectionDetail.revisedSection.emptyReason = autoChangedEmptyReasonChanged;
                if (autoChangedEmptyReasonChanged !== undefined &&
                    previousFinalValueForEmptyReason !== JSON.stringify(autoChangedEmptyReasonChanged) &&
                    !(!previousInitialValueForEmptyReason && !autoChangedEmptyReasonChanged)) {
                    console.log("resourceUpdate for Composition.section[" + sectionIndex + "].emptyReason");
                    updateChangeReport(dispatch, resourceChanges, compositionId,
                        "CodeableConcept",
                        "section[" + sectionIndex + "].emptyReason",
                        previousInitialValueForEmptyReason,
                        autoChangedEmptyReasonChanged);
                    dispatch(topSectionUpdated(newTopSectionDetail));
                    setCompositionResourceState(prevState => {
                        let newSection = JSON.parse(JSON.stringify(prevState.section));
                        newSection[sectionIndex].emptyReason = autoChangedEmptyReasonChanged;
                        return {
                            ...prevState,
                            section: newSection
                        }
                    });
                }
            }
            let generatedNarrative;
            if (section?.entry?.length === 1 && section.entry[0].reference?.split("/").length === 2 &&
                !isNaN(section.entry[0].reference.split("/")[1])) {
                let resourceFoi = section.entry[0].reference.split("/")[1];
                let resourceDictionary = {};
                for (const entry of resourceCollection) {
                    resourceDictionary[entry.id] = entry;
                }
                if (resourceDictionary[resourceFoi] && resourceDictionary[resourceFoi].resourceJson) {
                    let theResourceJson = resourceDictionary[resourceFoi].resourceJson;
                    generatedNarrative = generateResourceTextNarrative(theResourceJson);
                }
            }
            if (generatedNarrative) {
                setGeneratedNarrativeState(generatedNarrative);
            }
            let headerDisplay = section.title;
            if (!headerDisplay) {
                if (section.code) {
                    headerDisplay = simpleDisplayStringFromFHIRCodeableConcept(section.code) + " section";
                } else {
                    headerDisplay = "Untitled Section";
                }
            }
            setHeaderDisplayState(headerDisplay);
            let sectionCode = section?.code?.coding?.[0]?.code || section?.code?.text;
            let sectionFocusReference = section?.focus?.reference;
            let sectionPointer = sectionFocusReference || sectionCode;
            setSectionLoaded({
                "section": section, "sectionCode": sectionCode, "sectionPointer": sectionPointer,
                "compositionType": compositionType, "compositionTitle": compositionTitle,
                "generatedNarrative": generatedNarrative, "focus": section?.focus
            });
            if (compositionType === "SummaryOfNetEffect" &&
                (sectionCode === "column-headers" || sectionCode === "summary-of-findings" ||
                    sectionCode === "net-effect-estimate")
            ) {
                setNoneditableNetEffectSectionState(true);
            }
        }
    }, [topSectionJson, triggerRefresh, editMode]);

    useEffect(() => {
        if (editMode && topSection?.compositionId && sectionState.editTitle === false) {
            if (sectionState.title) {
                setHeaderDisplayState(sectionState.title);
            }
            let newTopSectionDetail = JSON.parse(JSON.stringify(topSection));
            let previousInitialValue = null;
            let previousFinalValue = null;
            previousInitialValue = newTopSectionDetail.initialSection?.title || null;
            previousFinalValue = newTopSectionDetail.revisedSection?.title || null;
            newTopSectionDetail.revisedSection.title = sectionState.title;
            if (sectionState.title !== undefined &&
                JSON.stringify(previousFinalValue) !== JSON.stringify(sectionState.title)) {
                console.log("resourceUpdate for Composition.section[" + sectionIndex + "].title");
                dispatch(topSectionUpdated(newTopSectionDetail));
                setCompositionResourceState(prevState => {
                    let newSection = JSON.parse(JSON.stringify(prevState.section));
                    newSection[sectionIndex].title = sectionState.title;
                    return {
                        ...prevState,
                        section: newSection
                    }
                });
                updateChangeReport(dispatch, resourceChanges, compositionId,
                    "string",
                    "section[" + sectionIndex + "].title",
                    previousInitialValue,
                    sectionState.title);
            }
        }
    }, [sectionState.title, sectionState.editTitle]);

    useEffect(() => {
        if (editMode && topSection?.compositionId) {
            let newTopSectionDetail = JSON.parse(JSON.stringify(topSection));
            let previousInitialValue = null;
            let previousFinalValue = null;
            previousInitialValue = newTopSectionDetail.initialSection?.code || null;
            previousFinalValue = JSON.stringify(newTopSectionDetail.revisedSection?.code || null);
            newTopSectionDetail.revisedSection.code = sectionState.code;
            if (sectionState.code !== undefined &&
                previousFinalValue !== JSON.stringify(sectionState.code)) {
                console.log("resourceUpdate for Composition.section[" + sectionIndex + "].code");
                dispatch(topSectionUpdated(newTopSectionDetail));
                setCompositionResourceState(prevState => {
                    let newSection = JSON.parse(JSON.stringify(prevState.section));
                    newSection[sectionIndex].code = sectionState.code;
                    return {
                        ...prevState,
                        section: newSection
                    }
                });
                updateChangeReport(dispatch, resourceChanges, compositionId,
                    "CodeableConcept",
                    "section[" + sectionIndex + "].code",
                    previousInitialValue,
                    sectionState.code);
            }
        }
    }, [sectionState.code]);

    useEffect(() => {
        if (editMode && topSection?.compositionId) {
            let newTopSectionDetail = JSON.parse(JSON.stringify(topSection));
            let previousInitialValue = null;
            let previousFinalValue = null;
            previousInitialValue = newTopSectionDetail.initialSection?.focus || null;
            previousFinalValue = JSON.stringify(newTopSectionDetail.revisedSection?.focus || null);
            newTopSectionDetail.revisedSection.focus = sectionState.focus;
            if (sectionState.focus !== undefined &&
                previousFinalValue !== JSON.stringify(sectionState.focus)) {
                console.log("resourceUpdate for Composition.section[" + sectionIndex + "].focus");
                dispatch(topSectionUpdated(newTopSectionDetail));
                setCompositionResourceState(prevState => {
                    let newSection = JSON.parse(JSON.stringify(prevState.section));
                    newSection[sectionIndex].focus = sectionState.focus;
                    return {
                        ...prevState,
                        section: newSection
                    }
                });
                updateChangeReport(dispatch, resourceChanges, compositionId,
                    "Reference",
                    "section[" + sectionIndex + "].focus",
                    previousInitialValue,
                    sectionState.focus);
            }
        }
    }, [sectionState.focus]);

    useEffect(() => {
        if (editMode && topSection?.compositionId) {
            let newTopSectionDetail = JSON.parse(JSON.stringify(topSection));
            let previousInitialValue = null;
            let previousFinalValue = null;
            previousInitialValue = newTopSectionDetail.initialSection?.text || null;
            previousFinalValue = JSON.stringify(newTopSectionDetail.revisedSection?.text || null);
            newTopSectionDetail.revisedSection.text = sectionState.text;
            if (sectionState.text !== undefined &&
                previousFinalValue !== JSON.stringify(sectionState.text) &&
                !(!previousInitialValue &&
                    JSON.stringify(sectionState.text) === JSON.stringify(emptyTextNoData))) {
                console.log("resourceUpdate for Composition.section[" + sectionIndex + "].text");
                let autoChangedEmptyReasonChanged = adjustEmptyReason(sectionState, setSectionState);
                if (autoChangedEmptyReasonChanged !== "no change") {
                    let previousInitialValueForEmptyReason = null;
                    let previousFinalValueForEmptyReason = null;
                    previousInitialValueForEmptyReason = newTopSectionDetail.initialSection?.emptyReason || null;
                    previousFinalValueForEmptyReason = JSON.stringify(newTopSectionDetail.revisedSection?.emptyReason || null);
                    newTopSectionDetail.revisedSection.emptyReason = autoChangedEmptyReasonChanged;
                    if (autoChangedEmptyReasonChanged !== undefined &&
                        previousFinalValueForEmptyReason !== JSON.stringify(autoChangedEmptyReasonChanged) &&
                        !(!previousInitialValueForEmptyReason && !autoChangedEmptyReasonChanged)) {
                        console.log("resourceUpdate for Composition.section[" + sectionIndex + "].emptyReason");
                        updateChangeReport(dispatch, resourceChanges, compositionId,
                            "CodeableConcept",
                            "section[" + sectionIndex + "].emptyReason",
                            previousInitialValue,
                            autoChangedEmptyReasonChanged);
                    } else {
                        autoChangedEmptyReasonChanged = "no change";
                    }
                }
                dispatch(topSectionUpdated(newTopSectionDetail));
                setCompositionResourceState(prevState => {
                    let newSection = JSON.parse(JSON.stringify(prevState.section));
                    newSection[sectionIndex].text = sectionState.text;
                    if (autoChangedEmptyReasonChanged !== "no change") {
                        newSection[sectionIndex].emptyReason = autoChangedEmptyReasonChanged;
                    }
                    return {
                        ...prevState,
                        section: newSection
                    }
                });
                updateChangeReport(dispatch, resourceChanges, compositionId,
                    "Narrative",
                    "section[" + sectionIndex + "].text",
                    previousInitialValue,
                    sectionState.text);
            }
        }
    }, [sectionState.text]);

    useEffect(() => {
        //console.log("TopSectionSliceDisplay useEffect due to change in sectionState.entry")
        if (editMode && topSection?.compositionId) {
            let newTopSectionDetail = JSON.parse(JSON.stringify(topSection));
            let previousInitialValue = null;
            let previousFinalValue = null;
            previousInitialValue = newTopSectionDetail.initialSection?.entry || null;
            previousFinalValue = JSON.stringify(newTopSectionDetail.revisedSection?.entry || null);
            newTopSectionDetail.revisedSection.entry = sectionState.entry;
            if (sectionState.entry !== undefined &&
                previousFinalValue !== JSON.stringify(sectionState.entry) &&
                !(!previousInitialValue &&
                    Array.isArray(sectionState.entry) &&
                    (sectionState.entry.length === 0 ||
                        (sectionState.entry.length === 1 && !sectionState.entry[0])
                    )
                )
            ) {
                console.log("resourceUpdate for Composition.section[" + sectionIndex + "].entry");
                let autoChangedEmptyReasonChanged = adjustEmptyReason(sectionState, setSectionState);
                if (autoChangedEmptyReasonChanged !== "no change") {
                    let previousInitialValueForEmptyReason = null;
                    let previousFinalValueForEmptyReason = null;
                    previousInitialValueForEmptyReason = newTopSectionDetail.initialSection?.emptyReason || null;
                    previousFinalValueForEmptyReason = JSON.stringify(newTopSectionDetail.revisedSection?.emptyReason || null);
                    newTopSectionDetail.revisedSection.emptyReason = autoChangedEmptyReasonChanged;
                    if (autoChangedEmptyReasonChanged !== undefined &&
                        previousFinalValueForEmptyReason !== JSON.stringify(autoChangedEmptyReasonChanged) &&
                        !(!previousInitialValueForEmptyReason && !autoChangedEmptyReasonChanged)) {
                        console.log("resourceUpdate for Composition.section[" + sectionIndex + "].emptyReason");
                        updateChangeReport(dispatch, resourceChanges, compositionId,
                            "CodeableConcept",
                            "section[" + sectionIndex + "].emptyReason",
                            previousInitialValue,
                            autoChangedEmptyReasonChanged);
                    } else {
                        autoChangedEmptyReasonChanged = "no change";
                    }
                }
                dispatch(topSectionUpdated(newTopSectionDetail));
                setCompositionResourceState(prevState => {
                    let newSection = JSON.parse(JSON.stringify(prevState.section));
                    newSection[sectionIndex].entry = sectionState.entry;
                    if (autoChangedEmptyReasonChanged !== "no change") {
                        newSection[sectionIndex].emptyReason = autoChangedEmptyReasonChanged;
                    }
                    return {
                        ...prevState,
                        section: newSection
                    }
                });
                updateChangeReport(dispatch, resourceChanges, compositionId,
                    "ArrayOfReference",
                    "section[" + sectionIndex + "].entry",
                    previousInitialValue,
                    sectionState.entry);
            }
        }
    }, [sectionState.entry]);

    useEffect(() => {
        if (editMode && topSection?.compositionId) {
            let newTopSectionDetail = JSON.parse(JSON.stringify(topSection));
            let previousInitialValue = null;
            let previousFinalValue = null;
            previousInitialValue = newTopSectionDetail.initialSection?.emptyReason || null;
            previousFinalValue = JSON.stringify(newTopSectionDetail.revisedSection?.emptyReason || null);
            newTopSectionDetail.revisedSection.emptyReason = sectionState.emptyReason;
            if (sectionState.emptyReason !== undefined &&
                previousFinalValue !== JSON.stringify(sectionState.emptyReason) &&
                !(!previousFinalValue && !sectionState.emptyReason)) {
                console.log("resourceUpdate for Composition.section[" + sectionIndex + "].emptyReason");
                dispatch(topSectionUpdated(newTopSectionDetail));
                setCompositionResourceState(prevState => {
                    let newSection = JSON.parse(JSON.stringify(prevState.section));
                    newSection[sectionIndex].emptyReason = sectionState.emptyReason;
                    return {
                        ...prevState,
                        section: newSection
                    }
                });
                updateChangeReport(dispatch, resourceChanges, compositionId,
                    "CodeableConcept",
                    "section[" + sectionIndex + "].emptyReason",
                    previousInitialValue,
                    sectionState.emptyReason);
            }
        }
    }, [sectionState.emptyReason]);

    useEffect(() => {
        if (editMode && topSection?.compositionId) {
            let newTopSectionDetail = JSON.parse(JSON.stringify(topSection));
            let previousInitialValue = null;
            let previousFinalValue = null;
            previousInitialValue = newTopSectionDetail.initialSection?.section || null;
            previousFinalValue = JSON.stringify(newTopSectionDetail.revisedSection?.section) || null;
            newTopSectionDetail.revisedSection.section = sectionState.section;
            if (sectionState.section !== undefined &&
                previousFinalValue !== JSON.stringify(sectionState.section) &&
                !(!previousInitialValue &&
                    Array.isArray(sectionState.section) &&
                    (sectionState.section.length === 0 ||
                        (sectionState.section.length === 1 && !sectionState.section[0])
                    )
                )
            ) {
                console.log("resourceUpdate for Composition.section[" + sectionIndex + "].section");
                let autoChangedEmptyReasonChanged = adjustEmptyReason(sectionState, setSectionState);
                if (autoChangedEmptyReasonChanged !== "no change") {
                    let previousInitialValueForEmptyReason = null;
                    let previousFinalValueForEmptyReason = null;
                    previousInitialValueForEmptyReason = newTopSectionDetail.initialSection?.emptyReason || null;
                    previousFinalValueForEmptyReason = JSON.stringify(newTopSectionDetail.revisedSection?.emptyReason || null);
                    newTopSectionDetail.revisedSection.emptyReason = autoChangedEmptyReasonChanged;
                    if (autoChangedEmptyReasonChanged !== undefined &&
                        previousFinalValueForEmptyReason !== JSON.stringify(autoChangedEmptyReasonChanged) &&
                        !(!previousInitialValueForEmptyReason && !autoChangedEmptyReasonChanged)) {
                        console.log("resourceUpdate for Composition.section[" + sectionIndex + "].emptyReason");
                        updateChangeReport(dispatch, resourceChanges, compositionId,
                            "CodeableConcept",
                            "section[" + sectionIndex + "].emptyReason",
                            previousInitialValue,
                            autoChangedEmptyReasonChanged);
                    } else {
                        autoChangedEmptyReasonChanged = "no change";
                    }
                }
                dispatch(topSectionUpdated(newTopSectionDetail));
                setCompositionResourceState(prevState => {
                    let newSection = JSON.parse(JSON.stringify(prevState.section));
                    newSection[sectionIndex].section = sectionState.section;
                    if (autoChangedEmptyReasonChanged !== "no change") {
                        newSection[sectionIndex].emptyReason = autoChangedEmptyReasonChanged;
                    }
                    return {
                        ...prevState,
                        section: newSection
                    }
                });
                updateChangeReport(dispatch, resourceChanges, compositionId,
                    "ArrayOfSection",
                    "section[" + sectionIndex + "].section",
                    previousInitialValue,
                    sectionState.section);
            }
        }
    }, [sectionState.section]);

    let timeTable;
    if (sectionState?.text?.div && sectionState.text.div.indexOf("<thead><tr><th>Day/Time (Eastern US)</th>") !== -1) {
        timeTable = convertTimeTable(sectionState.text.div);
    }

    return <>
        {(sectionState &&
            (sectionState.title || sectionState.text || sectionState.entry || sectionState.section || sectionState.emptyReason)
        ) &&
            <div id={sectionNavigationElementIds[sectionIndex]}>
                {(editMode && !noneditableNetEffectSectionState) ?
                    <>
                        {sectionState.editTitle ?
                            <>
                                <h4>Press Enter key when done editing the section title.</h4>
                                <TextField style={{ width: "100%" }} className="inputField"
                                    type='text' variant='outlined'
                                    placeholder='Enter section title.' value={sectionState.title}
                                    onKeyUp={(event) => {
                                        if (event.key === 'Enter') {
                                            setSectionState(prevState => {
                                                return {
                                                    ...prevState,
                                                    "editTitle": false
                                                }
                                            });
                                        }
                                    }}
                                    onChange={(e) => {
                                        setSectionState(prevState => {
                                            return {
                                                ...prevState,
                                                "title": e.target.value || ""
                                            }
                                        });
                                    }} />
                            </>
                            :
                            <h3>
                                <span>{sectionState.title || "Untitled Section (" + headerDisplayState + ")"}</span>
                                <span>  <button type="button" style={{ marginLeft: "12px" }}
                                    onClick={() => {
                                        setSectionState(prevState => {
                                            return {
                                                ...prevState,
                                                "editTitle": true
                                            }
                                        });
                                    }} >✎ Edit title</button></span>
                            </h3>
                        }</>
                    :
                    <h3>{headerDisplayState}{(noneditableNetEffectSectionState === true && editMode) &&
                        " -- this is not editable here. Use the Summary Table"}</h3>
                }
                <div style={{ marginLeft: "24px" }}>
                    <div>
                        {(editMode && !noneditableNetEffectSectionState) ?
                            <NarrativeEntry elementName="text"
                                startingValue={sectionState.text}
                                generatedNarrative={generatedNarrativeState}
                                calledFrom={"TopSectionSliceDisplay"}
                                setResourceState={setSectionState} />
                            :
                            <>{(sectionState.text?.div &&
                                sectionState.text.status !== "empty") &&
                                <>
                                    {timeTable ?
                                        <ReactMarkdown children={timeTable} rehypePlugins={[rehypeRaw]} />
                                        :
                                        <ReactMarkdown children={sectionState?.text?.div}
                                            rehypePlugins={[rehypeRaw]} />
                                    }
                                    <br />
                                </>}</>
                        }
                    </div>
                    {(editMode && !noneditableNetEffectSectionState) &&
                        <CodeableConceptEntry elementName="code" fieldLabel="Section Code"
                            startingValue={sectionState.code}
                            codeableConceptLevelValueSet={sectionCodeCodeableConceptSet}
                            setResourceState={setSectionState} startCollapsed />
                    }
                    {
                        (editMode && !noneditableNetEffectSectionState) ?
                            <>
                                <ReferenceEntry elementName="focus" fieldLabel="Section Focus"
                                    startingValue={sectionState.focus} enableCreation={true}
                                    setResourceState={setSectionState} deletable startCollapsed />
                            </>
                            :
                            <>
                                {sectionState.focus && <div>
                                    <div>
                                        <b>Section focus: </b>
                                        <DisplayFromFHIRReference reference={sectionState.focus} />
                                    </div>
                                    <p></p>
                                </div>}
                            </>
                    }
                    {(sectionState.entry?.length > 0 || sectionState.section?.length > 0 || editMode) &&
                        <>
                            <p></p>
                            <p style={{ marginBottom: "0px" }}><b>This section contains:</b></p>
                        </>
                    }
                    <div style={{ marginLeft: "24px" }}>
                        {(editMode && !noneditableNetEffectSectionState) ?
                            <>
                                {sectionLoaded?.section &&
                                    <SectionEntryArrayEntry sectionState={sectionState}
                                        setSectionState={setSectionState}
                                        sectionLoaded={sectionLoaded}
                                        foi={foi} slicePattern={true}
                                        revisedResourceJson={parentResourceDetail?.revisedResourceJson}
                                        resourceReferenceMap={resourceReferenceMap}
                                        associatedOutcomeMeasureReportState={associatedOutcomeMeasureReportState} />
                                }
                            </>
                            :
                            <>
                                {(sectionState.entry?.length > 0) &&
                                    <>
                                        {sectionState.entry.map((entry, entryIndex) => {
                                            return <span key={entryIndex}>
                                                <p>
                                                    <DisplayFromFHIRReference reference={entry} />
                                                </p>
                                            </span>
                                        })}
                                    </>}
                            </>
                        }
                        {(editMode && !noneditableNetEffectSectionState) && <div>
                            <Button className="formButton" style={{ margin: "0px" }}
                                content="Add/Delete/Reorganize Sections" compact
                                onClick={() => { setEditSectionModal({ "openModal": true }) }} />
                            <br />
                        </div>}
                        {editSectionModal.openModal && <EditSectionArrayModal editSectionModal={editSectionModal}
                            setEditSectionModal={setEditSectionModal}
                            sectionState={sectionState}
                            setSectionState={setSectionState} />}
                        {(sectionState.section?.length > 0) &&
                            sectionState.section.map((subsection, subsectionIndex) => {
                                return <div key={subsectionIndex}>
                                    <SectionDisplay sectionPath={[sectionIndex, subsectionIndex]}
                                        foi={foi} resourceReferenceMap={resourceReferenceMap}
                                        setParentSectionState={setSectionState} />
                                </div>
                            })}
                        {(editMode && !noneditableNetEffectSectionState) ?
                            <>
                                <CodeableConceptEntry elementName="emptyReason" fieldLabel="Empty Reason"
                                    startingValue={sectionState.emptyReason}
                                    codeableConceptLevelValueSet={emptyReasonCodeableConceptSet}
                                    setResourceState={setSectionState} deletable startCollapsed />
                            </>
                            :
                            <>{sectionState.emptyReason && <div>
                                <p>Section is empty:
                                    <span>{sectionState.emptyReason.coding?.[0]?.display} {sectionState.emptyReason.text}</span>
                                </p>
                            </div>}</>}
                    </div>
                </div>
            </div>}
    </>
}

export default TopSectionSliceDisplay;